import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Input, Button, PhoneInput } from '../../common';
import { Col, Divider, Flex, Row } from 'antd';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { getErrorString, currentBaseRoute, userType } from '../../utils';
import Authwrapper from './authwrapper';
import { USER_SIGNIN_PATH } from '../../constants/user-paths';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { registerUserApi } from '../../api/auth-user-api';
import Activation from './activation';
import { getSessionStorage, removeSingleCookie, setCookie, setSessionStorage } from '../../utils/cookies-util';
import GoogleSignIn from '../../components/google-signin';
import { setPatientUser } from '../../reducers/patient-user-slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentTenant } from 'reducers/selectors';
import { regex } from 'constants/generic-regex';

export const Signup = ({ handleLogin }) => {
  const formikRef = useRef();
  const [isActiationScreen, setIsActiationScreen] = useState(false);
  const [email, setEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tenantData } = useSelector(selectCurrentTenant);

  const params = new URLSearchParams(window.location.search);
  const affiliate_token = params.get('ref_token') ?? params.get('affiliate_token') ?? getSessionStorage('affiliate_token');

  // ✅ Inject Convert Experiences script on component mount
  useEffect(() => {
    const scriptId = 'convert-experiences-script';
    if (affiliate_token) {
      setSessionStorage('affiliate_token', affiliate_token);
    }

    // Prevent duplicate script injection
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = '//cdn-4.convertexperiments.com/v1/js/100414814-100416661.js?environment=production';
      script.type = 'text/javascript';
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Optional: clean up script when component unmounts
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const fromPage =
    location.state?.from?.pathname &&
    location.state.from.pathname !== '/' &&
    location.state?.from?.pathname?.includes(userType())
      ? location.state.from.pathname
      : currentBaseRoute();

  const getValidationSchema = () =>
    yup.object().shape({
      first_name: yup
        .string()
        .matches(regex.first_name, 'First name must contain only alphabets')
        .required('First name is required'),
      last_name: yup.string().matches(regex.last_name, 'Last name must contain only alphabets or a hyphen'),
      email: yup.string().required('Email is required').email('Valid Email is required'),
      phone_number: yup
        .string()
        .required('Phone number is required')
        .matches(/^\+?\d{10,15}$/, 'Valid Phone Number number is required'),
      password: yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(64, 'Password must not exceed 64 characters')
        .matches(regex.password, 'Password must contain only letters, numbers, and symbols. Spaces are not allowed.')
        .required('Password is required'),
      confirmPassword: yup
        .string()
        .required('Confirm Password is required')
        .oneOf([yup.ref('password'), null], 'Passwords must match'),
    });

  const getInitialValues = () => ({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
  });

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    try {
      const data = {
        user: {
          first_name: values.first_name,
          last_name: values?.last_name,
          email: values.email,
          phone_number: values.phone_number,
          password: values.password,
        },
        ...(affiliate_token ? { affiliate_token } : {}),
      };
      const res = await registerUserApi(data);
      if (res && res?.status) {
        toast.success(res?.message || 'Signup Successful!');
        // ✅ Inject Convert conversion tracking
        try {
          window._conv_q = window._conv_q || [];
          window._conv_q.push(['triggerConversion', '100498374']);
        } catch (err) {
          console.error('Convert conversion tracking failed', err);
        }
        if (res.headers['authorization']) {
          dispatch(setPatientUser(res?.data?.data ?? {}));
          setCookie('access-token-user', res.headers['authorization']);
          removeSingleCookie('resend-time');
          removeSingleCookie('resend-link-time');
          setSessionStorage('affiliate_token', '');
          handleLogin();
          navigate(fromPage, { replace: true });
        } else {
          setEmail(data?.user?.email);
          setCookie('resend-time', new Date().getTime() + 60 * 24 * 60);
          setIsActiationScreen(true);
        }
        return;
      } else {
        toast.error(res?.message ?? 'Signup Failed');
        setStatus({ message: res?.message });
        return;
      }
    } catch (error) {
      toast.error(getErrorString(error));
      setStatus({ message: getErrorString(error) });
    } finally {
      setSubmitting(false);
    }
  };

  if (isActiationScreen) {
    return <Activation setIsActiationScreen={setIsActiationScreen} email={email} />;
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={getInitialValues()}
      validationSchema={getValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
        <Flex vertical gap={36} justify="center" align="center">
          <img src={tenantData?.logo_url} width={160} />
          <Form autoComplete="off" onSubmit={handleSubmit} className={'authWrap'}>
            <Flex vertical gap={24} className={'authWrap'}>
              <Flex vertical justify="center" align="center" gap={8}>
                <h2 className="text-align-center heading-text">{`Sign up to ${tenantData?.name}`}</h2>
                <h5 className="text-align-center subheading-text">
                  To get started, please fill in the following details
                </h5>
              </Flex>
              <Flex vertical gap={16}>
                <Flex vertical style={{ marginBottom: '-12px' }}>
                  <GoogleSignIn handleLogin={handleLogin} fromPage={fromPage} isSignupPage={true} />
                  <Divider style={{ color: 'var(--user-neutral-7)' }} className="body-14-medium" plain>
                    or
                  </Divider>
                </Flex>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Input
                      className={'h5-regular input-box'}
                      placeholder="First Name"
                      name="first_name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.first_name}
                      errorMsg={errors.first_name && touched.first_name && errors.first_name}
                    />
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Input
                      className={'h5-regular input-box'}
                      placeholder="Last Name"
                      name="last_name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.last_name}
                      errorMsg={errors.last_name && touched.last_name && errors.last_name}
                    />
                  </Col>
                </Row>

                <Input
                  className={'h5-regular input-box'}
                  placeholder="you@example.com"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={(values?.email || '')?.replace(/\s/g, '')}
                  errorMsg={errors.email && touched.email && errors.email}
                />
                <PhoneInput
                  onChange={(e) => setFieldValue('phone_number', e)}
                  value={values?.phone_number}
                  errorMsg={errors.phone_number && touched.phone_number && errors.phone_number}
                />
                <Input
                  isPassword={true}
                  className={'h5-regular input-box'}
                  placeholder="Create Password"
                  name="password"
                  onChange={(e) => !e.target.value?.includes(' ') && handleChange(e)}
                  onBlur={handleBlur}
                  value={values?.password}
                  errorMsg={errors.password && touched.password && errors.password}
                />
                <Input
                  isPassword={true}
                  className={'h5-regular input-box'}
                  placeholder="Confirm Password"
                  name="confirmPassword"
                  onChange={(e) => !e.target.value?.includes(' ') && handleChange(e)}
                  onBlur={handleBlur}
                  value={values?.confirmPassword}
                  errorMsg={errors.confirmPassword && touched.confirmPassword && errors.confirmPassword}
                />
              </Flex>
              <Button
                style={{ marginBottom: '10px' }}
                text={'Sign Up'}
                type="primary"
                className={'h4-medium primary-button'}
                size="large"
                onClick={handleSubmit}
                loading={isSubmitting}
              />

              <Flex justify="center" align="center" gap={5}>
                <div className="text-navigate">Already have an account?</div>
                <Link className="button-navigate" to={USER_SIGNIN_PATH}>
                  Login
                </Link>
              </Flex>
            </Flex>
          </Form>
        </Flex>
      )}
    </Formik>
  );
};

export default function SignupUser({ handleLogin }) {
  return (
    <Authwrapper>
      <Signup handleLogin={handleLogin} />
    </Authwrapper>
  );
}
